import { FacetOption } from '@/types/FacetOption';
import FacetTypes from '@/types/search/enums/facetTypes';
import { FacetOverrides } from '@/types/search/searchQuery';

export type SearchFacet = {
    categories: {
        facets: SearchFacetItem[] | null;
    };
    count: number;
    id: string;
    name: string;
    options: FacetOption[] | undefined;
    range?: {
        max: number;
        min: number;
    };
    selected: boolean;
};

export type SearchFacetItem = {
    categories: SearchFacetItem[] | null;
    count: number;
    id: string;
    name: string;
    parents: null;
    selected: boolean;
};

export type FacetChipOption = {
    id?: string;
    label: string;
    value: FacetTypes;
};

export enum CurrentOpenMenu {
    AllFilters = 'all-filters',
    Category = 'category',
    Creator = 'creator',
    Location = 'location',
    MaterialsAndTechniques = 'materialsAndTechniques',
    Rating = 'rating',
    Shipping = 'shipping',
    StylePeriod = 'stylePeriod',
    Subject = 'subject',
}

export const MAX_FILTER_OPTIONS_COUNT = 11;

// "View More" expansion caps requested from search-party via
// Parameters.facetOverrides. Mirror the per-facet maximum from
// SEAR-1383's `aggregationsSizeMax`; search-party silently clamps
// over-cap values, but explicit caps here keep wire intent
// self-documenting.
export const AUCTION_HOUSE_VIEW_MORE_SIZE = 500;
export const COUNTRY_VIEW_MORE_SIZE = 500;
export const STANDARD_VIEW_MORE_SIZE = 100;

// Height at which Hammer UI <ViewMore> truncates the drawer-facet list
// and draws the toggle. Calibrated against live measurements: a
// Checkbox.Option row is ~30px, so 6 rows + gaps land at ~184px (200
// for AuctionHouse with the SearchInput's extra py-xs padding). The
// strict `scrollHeight > cutoff` check in <ViewMore> needs to clear
// 184, and we want the crop to land mid-6th-row so 5 rows show clean
// and the 6th peeks behind the fade gradient. 200 (the primitive's
// old default) sat right on the boundary and never fired the toggle.
// Don't bump this back up without re-measuring on a deployed stage.
export const VIEW_MORE_CUTOFF_HEIGHT = 170;

// Debounce delay before firing the AuctionHouse search-as-you-type request
// to search-party. Long enough that bursty typing doesn't generate one
// request per keystroke; short enough that the dropdown feels responsive.
export const AUCTION_HOUSE_TYPEAHEAD_DEBOUNCE_MS = 300;

// Per-facet bucket sizes bidder-react opts in to on the *initial* search
// request via `facetOverrides`. search-party's defaults (50-100 per facet)
// are a wire contract shared with other clients (sickOs/mobile, etc.); we
// can't lower them globally just because this client wants the fade-and-peek
// UI. Sending an explicit override per facet keeps the smaller buckets
// scoped to bidder-react. Creator is bumped to 7 to absorb the FE-side
// label-dedup in `filterFlatFacetOptionsOwnedByPrecedence` -- 7 returned
// minus the 1 typically dropped lands at 6 visible. View More click
// overrides on top of this initial map (size: 100/500) for the same facet,
// merging via `refetchWithFacetOverrides`. Attrs facets are intentionally
// not listed: their IDs are dynamic (`RegisterAttrsFacets` on the server)
// so they can't be enumerated statically, and the 50 they get from the
// server default is fine -- FacetCard's ViewMore truncates visually
// regardless of payload size.
export const INITIAL_FACET_OVERRIDES: FacetOverrides = {
    [FacetTypes.AUCTION_HOUSE_ID]: { size: 6 },
    [FacetTypes.COUNTRY_CODE_ID]: { size: 6 },
    [FacetTypes.CREATOR_ID]: { size: 7 },
    [FacetTypes.MATERIALS_AND_TECHNIQUES_ID]: { size: 6 },
    [FacetTypes.STYLE_PERIOD_ID]: { size: 6 },
};
